import { useEffect, useImperativeHandle, useState } from "react";
import styles from '@features/projects/styles/detailproject.module.scss';
import { useForm } from "react-hook-form";
import { Loader } from "@/features/users/components/Loader";
import usersStyles from "@/features/users/styles/users.module.scss";
import { ISection } from "@/api/projects/types";
import { IFormData } from "@/api/tasks/types";
import { DateRangePicker, RangeKeyDict } from "react-date-range";
import { formatCreatedAt, formatDateCreateTask } from "@/utils/hourMont";
import { useI18n } from "@/features/i18n/useI18n";
import { useCreateTask } from "@/features/projects/hooks/tasks/useCreateTask";
import { IProjectsData } from "@/api/projects/types";
import ProjectsSelect from "../../ProjectsSelect";
import SectionsSelect from "../../SectionsSelect";
import { Icon } from "@/assets/icons";
import { UserAssigneeSelector } from '@/components/UI';
import { useGetAllUsers } from '@/features/users/hooks/useGetAllUsers';
import { useCreateAssignment } from '@/features/assignments/hooks/useCreateAssignment';
import { IUsersData } from '@/features/users/slices/usersSlice';

export interface AddTaskProps {
  addNewTask: (form: IFormData) => void;
  closeAddTask: () => void;
  sections?: ISection[];
  projects?: IProjectsData[];
  sectionId?: string;
  ref?: React.Ref<{ resetForm: () => void }>;
}

export const AddTask = ({ addNewTask, closeAddTask, sections = [], projects = [], sectionId, ref }: AddTaskProps) => {
  const { t } = useI18n();
  const { isPending } = useCreateTask();
  const [sectionsList, setSectionsList] = useState(sections);
  const [projectsList, setProjectsList] = useState(projects);
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [taskDateRange, setTaskDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  // Estado para el usuario asignado
  const [assignedUser, setAssignedUser] = useState<{ id: string; name: string; avatarUrl?: string } | null>(null);
  // Estado para archivos adjuntos
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  
  // Obtener usuarios activos
  const { data: usersData, isLoading: isLoadingUsers } = useGetAllUsers(1);
  const users: IUsersData[] = usersData?.data || [];
  const userOptions = users.map(u => ({
    id: String(u.userid),
    name: `${u.firstname} ${u.firstlastname}`.trim(),
    avatarUrl: undefined // o u.picture si existe
  }));
  // Hook para crear asignación
  const { mutateAsync: createAssignment } = useCreateAssignment();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
    setValue,
    trigger,
    reset,
  } = useForm<IFormData>({
    defaultValues: {
      projectId: "",
      sectionId: sectionId ? sectionId : "",
      title: "",
      description: "",
      state: "incompleta",
      startDate: formatDateCreateTask(taskDateRange[0].startDate),
      endDate: formatDateCreateTask(taskDateRange[0].endDate),
      createdAt: formatCreatedAt(new Date()),
      parentTaskId: "",
    },
  });

  useEffect(() => {
    setSectionsList(sections);
    setProjectsList(projects);
  }, [projects, sections]);

  // Función para manejar la carga de archivos
  const handleFileUpload = async (files: File[]) => {
    try {
      // Aquí puedes implementar la lógica de carga de archivos
      // Por ahora solo agregamos los archivos al estado
      setAttachedFiles(prev => [...prev, ...files]);
      console.log('Archivos seleccionados:', files);
    } catch (error) {
      console.error('Error al cargar archivos:', error);
    }
  };

  // Función para remover archivos
  const handleFileRemove = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateTask = async (data: IFormData) => {
    data.sectionId = data.sectionId.toString();
    data.projectId = data.projectId.toString();
    
    // Crear la tarea
    const result = await addNewTask(data);
    
    // Si hay archivos adjuntos, procesarlos después de crear la tarea
    if (attachedFiles.length > 0 && result && typeof result === 'object' && 'id' in result) {
      // Aquí puedes implementar la lógica para subir archivos a la tarea creada
      console.log('Subiendo archivos a la tarea:', attachedFiles);
    }
    
    // Si hay usuario asignado y la tarea se creó correctamente
    if (assignedUser && result && typeof result === 'object' && 'id' in result) {
      const selected = users.find(u => String(u.userid) === assignedUser.id);
      if (selected) {
        const formData = {
          taskId: (result as { id: string }).id,
          taskTitle: data.title || '',
          username: selected.username,
          fullName: `${selected.firstname} ${selected.firstlastname} ${selected.secondlastname}`.trim(),
          startDate: data.startDate || null,
          endDate: data.endDate || null,
        };
        try {
          await createAssignment(formData);
        } catch {
          // Manejo de error opcional
        }
      }
    }
  }

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      reset();
      setAttachedFiles([]);
    }
  }));

  const handleCancel = () => {
    reset();
    setAttachedFiles([]);
    closeAddTask();
  };

  useEffect(() => {
    setFocus("title");
  }, [setFocus]);

  const dateLabel = () => {
    return `${taskDateRange[0].startDate.toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} - 
    ${taskDateRange[0].endDate.toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
  }

  const changeDate = (item: RangeKeyDict) => {
    if (item.selection) {
      setTaskDateRange([item.selection]);
      // Update form values
      setValue('startDate', formatDateCreateTask(item.selection.startDate));
      setValue('endDate', formatDateCreateTask(item.selection.endDate));
    }
  };

  return (
    <dialog className={styles["addtask"]}>
      {isPending && <Loader />}
      <form onSubmit={handleSubmit(handleCreateTask)}>
        {/* Selector de responsable */}
        <div className={styles["addtask__groupform"]}>
          <span className={styles["addtask__label"]}>Responsable:</span>
          <UserAssigneeSelector
            users={userOptions}
            assignedUser={assignedUser}
            onAssign={setAssignedUser}
            onUnassign={() => setAssignedUser(null)}
            isLoading={isLoadingUsers}
            placeholder="Buscar usuario..."
          />
        </div>
        <label className={styles["addtask__label"]} htmlFor="taskName">
          Nombre de la tarea:
          <input
            id="taskName"
            className={styles["addtask__input"]}
            {...register("title", { required: t("tasks_module.modal_create_task_error_name") })}
            placeholder={"Nombre de la tarea"}
          />
          {errors.title && (
            <p className={usersStyles["formusers__requerid"]}>
              {errors.title.message}
            </p>
          )}
        </label>
        <span className={styles["addtask__label"]}>
          Fecha limite:
          <button type="button" onClick={() => setShowDatePicker(!showDatePicker)} className={styles["addtask__input"]}
            name="activador configuración fecha limite"
          >
            {dateLabel()}
          </button>
          {showDatePicker ?
            <button className={styles["addtask__date-range"]} onMouseLeave={() => setShowDatePicker(false)} type="button">
              <DateRangePicker
                onChange={(item: RangeKeyDict) => changeDate(item)}
                ranges={taskDateRange}
                dateDisplayFormat={"dd MMM yyyy"}
                rangeColors={["#80E593"]}
              />
            </button>
            : null
          }
        </span>
        {projects?.length > 0 &&
          <div className={styles["addtask__groupform"]}>
            <span className={styles["addtask__label"]}>{t("module_projects.module_title")}: </span>
            <ProjectsSelect
              classNameError={usersStyles["formusers__requerid"]}
              name="projectId"
              setValue={setValue}
              trigger={trigger}
              errorMessage={errors.projectId?.message}
              list={projectsList}
              setProjectList={setProjectsList}
              originalList={projects}
              placeHolder={t("module_projects.module_title")}
            />
          </div>
        }
        {!sectionId && sections?.length > 0 &&
          <div className={styles["addtask__groupform"]}>
            <span className={styles["addtask__label"]}>Secciónes: </span>
            <SectionsSelect
              classNameError={usersStyles["formusers__requerid"]}
              name="sectionId"
              setValue={setValue}
              trigger={trigger}
              errorMessage={errors.sectionId?.message}
              list={sectionsList}
              setProjectList={setSectionsList}
              originalList={sections}
              placeHolder={t("module_projects.modal_placeholder_sections")}
            />
          </div>
        }
        
        {/* Módulo de carga de archivos */}
        <div className={styles["addtask__groupform"]}>
          <span className={styles["addtask__label"]}>Archivos adjuntos:</span>
          <div className={styles["addtask__files"]}>
            {/* Debug: Verificar que esta sección se renderiza */}
            <div style={{ border: '1px solid red', padding: '10px', margin: '10px 0', backgroundColor: 'yellow' }}>
              <p>Debug: Sección de archivos visible</p>
              <p>Archivos adjuntos: {attachedFiles.length}</p>
            </div>
            
            {/* Componente FileUpload simplificado para debug */}
            <div style={{ 
              border: '2px dashed #ccc', 
              padding: '20px', 
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              cursor: 'pointer',
              margin: '10px 0'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📁</div>
              <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Arrastra archivos aquí o haz clic para seleccionar</p>
              <p style={{ margin: '5px 0', color: '#007bff', textDecoration: 'underline' }}>Seleccionar archivos</p>
              <p style={{ margin: '5px 0', fontSize: '0.8rem', color: '#666' }}>Formatos soportados: PDF, imágenes, documentos</p>
              
              <input
                type="file"
                multiple={true}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.doc,.docx"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    handleFileUpload(Array.from(files));
                  }
                }}
                style={{ display: 'none' }}
                id="file-upload-debug"
              />
              <button 
                type="button"
                onClick={() => document.getElementById('file-upload-debug')?.click()}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Hacer clic aquí para seleccionar archivos
              </button>
            </div>
            
            {/* Lista de archivos adjuntos */}
            {attachedFiles.length > 0 && (
              <div className={styles["addtask__files-list"]}>
                <h4>Archivos seleccionados:</h4>
                {attachedFiles.map((file, index) => (
                  <div key={index} className={styles["addtask__file-item"]}>
                    <span className={styles["addtask__file-name"]}>{file.name}</span>
                    <span className={styles["addtask__file-size"]}>
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      onClick={() => handleFileRemove(index)}
                      className={styles["addtask__file-remove"]}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <input type="hidden" {...register("projectId", { required: t("tasks_module.modal_create_task_error_project") })} />
        <input type="hidden" {...register("sectionId", { required: t("assignments_module.modal_task_create_error_section") })} />
        <div className={styles["addtask__buttons"]}>
          <button tabIndex={0} className={styles["addtask__button-cancel"]} onClick={handleCancel}
            type="button"
          >
            Cancelar
          </button>
          <button tabIndex={0} className={styles["addtask__button-confirm"]} type="submit">
            <Icon name="save" /><span>Guardar</span>
          </button>
        </div>
      </form>
    </dialog>
  );
};
